import { useEffect, useRef } from 'react';
import { ApiError } from '../services/api';

interface VisiblePollingOptions {
    intervalMs: number;
    enabled?: boolean;
    immediate?: boolean;
    onError?: (error: unknown) => void;
}

export const getPollingRetryDelayMs = (error: unknown, fallbackMs: number): number => {
    if (error instanceof ApiError && error.retryAfterSeconds) {
        return Math.max(1000, error.retryAfterSeconds * 1000);
    }
    return fallbackMs;
};

export const useVisiblePolling = (
    callback: () => Promise<void>,
    {
        intervalMs,
        enabled = true,
        immediate = true,
        onError
    }: VisiblePollingOptions
): void => {
    const callbackRef = useRef(callback);
    const onErrorRef = useRef(onError);
    const timeoutRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        if (!enabled) return;

        let cancelled = false;

        const clearTimer = () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const schedule = (delayMs: number) => {
            clearTimer();
            if (cancelled || document.hidden) return;
            timeoutRef.current = window.setTimeout(() => {
                void poll();
            }, delayMs);
        };

        const poll = async () => {
            if (cancelled || document.hidden) return;
            if (inFlightRef.current) {
                schedule(intervalMs);
                return;
            }

            inFlightRef.current = true;
            let nextDelay = intervalMs;

            try {
                await callbackRef.current();
            } catch (error) {
                nextDelay = getPollingRetryDelayMs(error, intervalMs);
                onErrorRef.current?.(error);
            } finally {
                inFlightRef.current = false;
                schedule(nextDelay);
            }
        };

        const handleVisibilityChange = () => {
            clearTimer();
            if (!document.hidden) void poll();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        if (immediate && !document.hidden) {
            void poll();
        } else {
            schedule(intervalMs);
        }

        return () => {
            cancelled = true;
            clearTimer();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, immediate, intervalMs]);
};
